import { assign, setup } from "xstate";
import type { KioskPrescriptionIdentification } from "@ascleo/frontdesk-contracts/kiosk-operations/prescription-request";

/**
 * Card data from eGK read - patient demographics
 *
 * @since 1.0.0
 */
export interface PrescriptionCardData {
  patientId?: string;
  familyName: string;
  givenName: string;
}

/**
 * Prescription summary from bridge
 *
 * @since 1.0.0
 */
export interface PrescriptionSummary {
  id: string;
  actType: string;
  patientId: string;
  medicationName: string;
  status: string;
  prescriptionDate?: string;
  content?: string;
  comment?: string;
  employeeName?: string;
}

/**
 * Why the patient needs the prescription — the kiosk reason grid. Sent to the
 * bridge, which records a staff-facing German label on the request.
 *
 * @since 1.0.0
 */
export type PrescriptionRequestReason =
  | "schmerzen"
  | "entzuendung"
  | "nachsorge"
  | "allergie"
  | "chronisch"
  | "anderes";

/**
 * How urgently the patient needs the prescription — the kiosk urgency pills.
 * Drives the Doc-Cirrus task urgency level.
 *
 * @since 1.0.0
 */
export type PrescriptionUrgency = "soon" | "tomorrow" | "week";

/**
 * Prescription flow context — combined view + order
 *
 * @since 1.0.0
 */
export interface PrescriptionFlowContext {
  // Patient identification
  patientId?: string;
  identification?: KioskPrescriptionIdentification;
  // Previous prescription hints for the request form
  prescriptions?: readonly PrescriptionSummary[];
  prescriptionsCount?: number;
  // Ordering
  insuranceType?: "PUBLIC" | "PRIVATE";
  /** Free-text medication request typed by the patient (single request area) */
  requestText?: string;
  /** Selected reason for the request (kiosk reason grid) */
  requestReason?: PrescriptionRequestReason;
  /** Selected urgency for the request (kiosk urgency pills) */
  requestUrgency?: PrescriptionUrgency;
  /** Whether the patient explicitly requested an e-mail after staff processing. */
  completionEmailRequested: boolean;
  /** Patient-entered destination used only for the requested completion message. */
  completionEmail?: string;
  quarterlyEgkStatus?: "confirmed" | "warning" | "blocked";
  requestSubmitted: boolean;
  // Error state
  errorMessage?: string;
}

/**
 * Prescription flow events — combined view + order
 *
 * @since 1.0.0
 */
export type PrescriptionFlowEvent =
  // Navigation
  | { type: "START" }
  | { type: "BACK" }
  | { type: "DONE" }
  // Card read / patient identification
  | {
      type: "CARD_OK";
      patientId: string;
      crlogId: string;
      insuranceType?: "PUBLIC" | "PRIVATE";
    }
  | { type: "MANUAL_LOOKUP" }
  | {
      type: "MANUAL_PATIENT_FOUND";
      patientId: string;
      insuranceType: "PUBLIC" | "PRIVATE";
    }
  | { type: "CARD_MISSING" }
  | { type: "NO_PATIENT_FOUND" }
  // Prescription history for request prefill
  | {
      type: "PRESCRIPTIONS_LOADED";
      prescriptions: readonly PrescriptionSummary[];
    }
  | { type: "PRESCRIPTIONS_EMPTY" }
  // Ordering
  | { type: "SET_REQUEST_TEXT"; text: string }
  | { type: "SET_REASON"; reason: PrescriptionRequestReason }
  | { type: "SET_URGENCY"; urgency: PrescriptionUrgency }
  | { type: "SET_COMPLETION_EMAIL_REQUESTED"; requested: boolean }
  | { type: "SET_COMPLETION_EMAIL"; email: string }
  | { type: "SUBMIT_REQUEST" }
  | { type: "REQUEST_SUCCESS" }
  | { type: "REQUEST_POLICY_RESTRICTED" }
  | { type: "REQUEST_ERROR"; message: string };

/**
 * Prescription flow state identifiers
 *
 * @since 1.0.0
 */
export type PrescriptionFlowStateId =
  | "cardCheck"
  | "patientLookup"
  | "medicationSelection"
  | "manualMedicationRequest"
  | "requestReview"
  | "requestSubmitting"
  | "requestConfirmation"
  | "manualHandoff"
  | "noPatientFound"
  | "done";

/**
 * Initial context for the prescription flow
 */
const initialContext: PrescriptionFlowContext = {
  patientId: undefined,
  identification: undefined,
  prescriptions: undefined,
  prescriptionsCount: undefined,
  insuranceType: undefined,
  quarterlyEgkStatus: undefined,
  errorMessage: undefined,
  requestText: undefined,
  requestReason: undefined,
  requestUrgency: undefined,
  completionEmailRequested: false,
  completionEmail: undefined,
  requestSubmitted: false,
};

/**
 * Prescription flow XState machine — combined view + order
 *
 * Orchestrates the combined prescription self-service flow:
 * 1. Welcome screen
 * 2. Card reader authentication (+ name/DOB fallback)
 * 3. Medication request form with inline history helpers
 * 4. Review & submit
 * 5. Confirmation
 *
 * @since 1.0.0
 */
export const prescriptionFlowMachine = setup({
  types: {
    context: {} as PrescriptionFlowContext,
    events: {} as PrescriptionFlowEvent,
  },
  actions: {
    setPatientFromCard: assign(({ event }) => {
      if (event.type !== "CARD_OK") return {};
      return {
        ...initialContext,
        patientId: event.patientId,
        identification: {
          kind: "egk" as const,
          crlogId: event.crlogId,
        },
        insuranceType: event.insuranceType,
        // eGK read at kiosk => quarterly confirmed
        quarterlyEgkStatus: "confirmed" as const,
      };
    }),
    setPatientFromManualLookup: assign(({ event }) => {
      if (event.type !== "MANUAL_PATIENT_FOUND") return {};
      return {
        ...initialContext,
        patientId: event.patientId,
        identification: { kind: "manual" as const },
        insuranceType: event.insuranceType,
        quarterlyEgkStatus: undefined,
        prescriptions: undefined,
        prescriptionsCount: undefined,
      };
    }),
    setPrescriptions: assign(({ event }) => {
      if (event.type === "PRESCRIPTIONS_LOADED") {
        return {
          prescriptions: event.prescriptions,
          prescriptionsCount: event.prescriptions.length,
        };
      }
      if (event.type === "PRESCRIPTIONS_EMPTY") {
        return {
          prescriptions: [],
          prescriptionsCount: 0,
        };
      }
      return {};
    }),
    setRequestText: assign(({ event }) => {
      if (event.type !== "SET_REQUEST_TEXT") return {};
      return { requestText: event.text };
    }),
    setReason: assign(({ event }) => {
      if (event.type !== "SET_REASON") return {};
      return { requestReason: event.reason };
    }),
    setUrgency: assign(({ event }) => {
      if (event.type !== "SET_URGENCY") return {};
      return { requestUrgency: event.urgency };
    }),
    setCompletionEmailRequested: assign(({ context, event }) => {
      if (event.type !== "SET_COMPLETION_EMAIL_REQUESTED") return {};
      return {
        completionEmailRequested: event.requested,
        completionEmail: event.requested ? context.completionEmail : "",
      };
    }),
    setCompletionEmail: assign(({ event }) => {
      if (event.type !== "SET_COMPLETION_EMAIL") return {};
      return { completionEmail: event.email };
    }),
    // Clearing the error is part of succeeding: a retry that works must not
    // leave the previous attempt's message behind for the confirmation screen.
    setRequestSubmitted: assign({
      requestSubmitted: true,
      errorMessage: undefined,
    }),
    setError: assign(({ event }) => {
      if (event.type === "REQUEST_ERROR") {
        return { errorMessage: event.message };
      }
      return { errorMessage: undefined };
    }),
    resetContext: assign(() => ({ ...initialContext })),
  },
  guards: {
    isManualIdentification: ({ context }) =>
      context.identification?.kind === "manual",
  },
}).createMachine({
  id: "prescriptionFlow",
  initial: "cardCheck",
  context: initialContext,
  states: {
    cardCheck: {
      on: {
        CARD_OK: {
          target: "medicationSelection",
          actions: "setPatientFromCard",
        },
        MANUAL_LOOKUP: {
          target: "patientLookup",
        },
        CARD_MISSING: { target: "noPatientFound" },
        NO_PATIENT_FOUND: { target: "noPatientFound" },
      },
    },
    patientLookup: {
      on: {
        BACK: { target: "cardCheck" },
        MANUAL_PATIENT_FOUND: {
          target: "manualMedicationRequest",
          actions: "setPatientFromManualLookup",
        },
        NO_PATIENT_FOUND: { target: "noPatientFound" },
      },
    },
    medicationSelection: {
      on: {
        BACK: {
          target: "cardCheck",
          actions: "resetContext",
        },
        PRESCRIPTIONS_LOADED: {
          actions: "setPrescriptions",
        },
        PRESCRIPTIONS_EMPTY: {
          actions: "setPrescriptions",
        },
        SET_REQUEST_TEXT: {
          actions: "setRequestText",
        },
        SET_REASON: {
          actions: "setReason",
        },
        SET_URGENCY: {
          actions: "setUrgency",
        },
        SET_COMPLETION_EMAIL_REQUESTED: {
          actions: "setCompletionEmailRequested",
        },
        SET_COMPLETION_EMAIL: {
          actions: "setCompletionEmail",
        },
        SUBMIT_REQUEST: {
          target: "requestReview",
        },
      },
    },
    manualMedicationRequest: {
      on: {
        BACK: {
          target: "patientLookup",
          actions: "resetContext",
        },
        SET_REQUEST_TEXT: {
          actions: "setRequestText",
        },
        SET_REASON: {
          actions: "setReason",
        },
        SET_URGENCY: {
          actions: "setUrgency",
        },
        SET_COMPLETION_EMAIL_REQUESTED: {
          actions: "setCompletionEmailRequested",
        },
        SET_COMPLETION_EMAIL: {
          actions: "setCompletionEmail",
        },
        SUBMIT_REQUEST: {
          target: "requestReview",
        },
      },
    },
    requestReview: {
      on: {
        SET_COMPLETION_EMAIL_REQUESTED: {
          actions: "setCompletionEmailRequested",
        },
        SET_COMPLETION_EMAIL: {
          actions: "setCompletionEmail",
        },
        BACK: [
          {
            guard: "isManualIdentification",
            target: "manualMedicationRequest",
          },
          { target: "medicationSelection" },
        ],
        SUBMIT_REQUEST: {
          target: "requestSubmitting",
        },
      },
    },
    requestSubmitting: {
      on: {
        REQUEST_SUCCESS: {
          target: "requestConfirmation",
          actions: "setRequestSubmitted",
        },
        REQUEST_ERROR: {
          target: "requestReview",
          actions: "setError",
        },
        REQUEST_POLICY_RESTRICTED: {
          target: "manualHandoff",
        },
      },
    },
    manualHandoff: {
      on: {
        DONE: {
          target: "cardCheck",
          actions: "resetContext",
        },
      },
    },
    requestConfirmation: {
      on: {
        DONE: {
          target: "done",
        },
      },
    },
    noPatientFound: {
      on: {
        BACK: { target: "cardCheck" },
        DONE: {
          target: "cardCheck",
          actions: "resetContext",
        },
      },
    },
    done: {
      entry: "resetContext",
      on: {
        DONE: {
          target: "cardCheck",
          actions: "resetContext",
        },
      },
    },
  },
});
